import React from 'react';
import { Plus, MessageCircle, Trash2 } from 'lucide-react';
import { colors, actionButtonStyle } from '../../styles/designTokens';

// ── Styles ──────────────────────────────────────────────────

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
};

const addEventsStyle = {
  ...actionButtonStyle,
  minWidth: '44px',
  minHeight: '44px',
  backgroundColor: 'transparent',
  color: colors.success,
  borderColor: colors.success,
};

const askDanStyle = {
  ...actionButtonStyle,
  minWidth: '44px',
  minHeight: '44px',
  backgroundColor: 'transparent',
  color: '#7c3aed',
  borderColor: '#7c3aed',
  position: 'relative',
};

const deleteStyle = {
  ...actionButtonStyle,
  minWidth: '44px',
  minHeight: '44px',
  backgroundColor: 'transparent',
  color: colors.urgent,
  borderColor: colors.urgent,
};

const badgeStyle = {
  position: 'absolute',
  top: '-6px',
  right: '-6px',
  backgroundColor: '#7c3aed',
  color: '#ffffff',
  fontSize: '10px',
  fontWeight: 600,
  minWidth: '18px',
  height: '18px',
  borderRadius: '9px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 4px',
  lineHeight: 1,
};

const mobileDeleteStyle = {
  ...actionButtonStyle,
  minWidth: '44px',
  minHeight: '44px',
  backgroundColor: 'transparent',
  color: colors.urgent,
  borderColor: colors.urgent,
  marginTop: '8px',
};

/**
 * TripActions — Desktop action control group and mobile delete affordance.
 *
 * Renders three desktop controls (Add Events, Ask Dan with gap badge, Delete Trip)
 * and a mobile-reachable delete control positioned near the top of the card body
 * so it's accessible without scrolling (Req 4.2).
 *
 * Props:
 * @param {() => void} onAddEvents — Opens the Edit Trip Modal
 * @param {() => void} onAskDan — Opens the AI chat assistant
 * @param {() => void} onDeleteTrip — Initiates the delete confirmation flow
 * @param {number} gapCount — Number of trip gaps for the badge indicator
 */
function TripActions({ onAddEvents, onAskDan, onDeleteTrip, gapCount }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <>
      {/* Desktop action controls */}
      {!isMobile && (
        <div style={containerStyle} data-testid="trip-actions-desktop">
          <button
            style={addEventsStyle}
            onClick={onAddEvents}
            data-testid="action-add-events"
            title="Add events to this trip"
            aria-label="Add events"
          >
            <Plus size={14} aria-hidden="true" />
            Add Events
          </button>

          <button
            style={askDanStyle}
            onClick={onAskDan}
            data-testid="action-ask-dan"
            title="Ask Dan - AI travel assistant"
            aria-label={gapCount > 0 ? `Ask Dan, ${gapCount} trip gaps` : 'Ask Dan'}
          >
            <MessageCircle size={14} aria-hidden="true" />
            Ask Dan
            {gapCount > 0 && (
              <span style={badgeStyle} data-testid="gap-badge" aria-hidden="true">
                {gapCount}
              </span>
            )}
          </button>

          <button
            style={deleteStyle}
            onClick={onDeleteTrip}
            data-testid="action-delete-trip"
            title="Delete this trip"
            aria-label="Delete trip"
          >
            <Trash2 size={14} aria-hidden="true" />
            Delete Trip
          </button>
        </div>
      )}

      {/* Mobile delete affordance — reachable without scrolling (Req 4.2) */}
      {isMobile && (
        <button
          style={mobileDeleteStyle}
          onClick={onDeleteTrip}
          data-testid="mobile-action-delete-trip"
          title="Delete this trip"
          aria-label="Delete trip"
        >
          <Trash2 size={14} aria-hidden="true" />
          Delete Trip
        </button>
      )}
    </>
  );
}

export default TripActions;
